import type { AccountAccessState } from './billing.js'
import type { PickerOptionAvailabilityState } from './commandAvailability.js'

export type AccountMachineState = 'local_only' | 'sync_active' | 'sync_terminating' | 'sync_deactivated'
export type AccountCloudPosture = 'detached' | 'connected-verified' | 'offline-unverified'
export type SessionAcceptanceDisposition = 'keep' | 'mount' | 'reject'
export const SESSION_ACCEPTANCE_INGRESSES = [
  'startup',
  'observer',
  'otp',
  'stripe-poll',
  'stripe-continue',
  'profile-refresh',
  'recovery'
] as const
export type SessionAcceptanceIngress = (typeof SESSION_ACCEPTANCE_INGRESSES)[number]
export type SessionAcceptanceInput = {
  passportOwnerId: string | null
  sessionOwnerId: string | null
  verifiedProfileAccountState: AccountAccessState | null
  isCloudReachable: boolean
  entryCommitted: boolean
}
export type SessionAcceptanceDecision = {
  disposition: SessionAcceptanceDisposition
  posture: AccountCloudPosture
}
export type AccountExitSignalIntent = 'explicit' | 'passive'
export type AccountExitSignalReason = 'logout' | 'account-delete' | 'auth-loss'
export type AccountExitSignal = {
  type: 'account-exit'
  ownerId: string
  mountId: string
  sourceTabId: string
  intent: AccountExitSignalIntent
  reason: AccountExitSignalReason
  issuedAt: string
}
export type AccountOwnerAuthority = {
  mountedAccountOwnerId: string | null
  acceptedSessionOwnerId: string | null
  hasVerifiedProfileAuthority: boolean
}
export type AccountAuthorityFacts = AccountOwnerAuthority & {
  isCloudReachable: boolean
}
export type SyncCommandAction =
  | 'account-activate-sync'
  | 'user-login'
  | 'account-cancel-sync'
  | 'user-rename'
  | 'account-delete-all!'
  | 'index-export-all'
  | 'space-export-file'
  | 'space-open-file'
  | 'space-import-file'
  | 'space-new'
  | 'space-rename'
  | 'space-delete-!'
export type PausedSyncAction = 'reactivate sync' | 'export sync shelf' | 'log out'
export type SyncSurfaceAction = SyncCommandAction | PausedSyncAction
export type SyncSurfaceActionAvailability = {
  action: SyncSurfaceAction
  state: PickerOptionAvailabilityState
}
export type SyncControlIntent = 'cancel' | 'reactivate'
export type AccountSyncCommandId = 'account-activate-sync' | 'account-cancel-sync'
export type SyncControlApiError =
  | 'invalid_token'
  | 'missing_token'
  | 'subscription_missing'
  | 'account_not_sync'
  | 'profile_update_failed'
  | 'unknown'
  | string

export type AccountIntent =
  | 'ACTIVATE_SYNC'
  | 'TERMINATE_SYNC'
  | 'REACTIVATE_SYNC'
  | 'EXPORT_SHELF'
  | 'DELETE_ACCOUNT'
  | 'LOGOUT'

export type AccountSnapshot = AccountOwnerAuthority & {
  hasUsername: boolean
  isSyncPaired: boolean
  isIndexSpace: boolean
  isViewMode: boolean
  accountState: AccountAccessState
  hasSyncPlan: boolean
  stripeCancelAtPeriodEnd: boolean
  stripeCurrentPeriodEnd?: string
}

export type SyncIndexStatusPresentation = {
  label: string
  feedback: string
  description: string
}

export type AccountMachineRuntimeState = {
  lifecycle: AccountMachineState
  pendingIntent?: AccountIntent
  lastIntent?: AccountIntent
  lastError?: string
}

export type AccountMachineEvent =
  | { type: 'INTENT', intent: AccountIntent }
  | { type: 'REQUEST_OK', intent: AccountIntent }
  | { type: 'REQUEST_FAIL', intent: AccountIntent, error?: string }
  | { type: 'PROFILE_REFRESHED', snapshot: AccountSnapshot }
  | { type: 'CLOUD_AUTHORITY_LOST', snapshot: AccountSnapshot }

export const PAUSED_SYNC_ACTIONS: readonly PausedSyncAction[] = [
  'reactivate sync',
  'export sync shelf',
  'log out'
]

export function hasMountedAccount(input: Pick<AccountOwnerAuthority, 'mountedAccountOwnerId'>): boolean {
  return Boolean(input.mountedAccountOwnerId)
}

export function hasAcceptedSession(input: Pick<AccountOwnerAuthority, 'acceptedSessionOwnerId'>): boolean {
  return Boolean(input.acceptedSessionOwnerId)
}

export function hasMatchingAcceptedSession(input: AccountOwnerAuthority): boolean {
  return Boolean(
    input.mountedAccountOwnerId
    && input.acceptedSessionOwnerId === input.mountedAccountOwnerId
  )
}

export function hasVerifiedCloudAuthority(input: AccountOwnerAuthority): boolean {
  return hasMatchingAcceptedSession(input) && input.hasVerifiedProfileAuthority
}

export function resolveAccountCloudPosture(input: AccountAuthorityFacts): AccountCloudPosture {
  if (!hasMountedAccount(input)) return 'detached'
  if (hasVerifiedCloudAuthority(input) && input.isCloudReachable) return 'connected-verified'
  return 'offline-unverified'
}

export function resolveSessionAcceptance(input: SessionAcceptanceInput): SessionAcceptanceDecision {
  const hasPassport = input.passportOwnerId !== null
  const hasSession = input.sessionOwnerId !== null
  const hasVerifiedAccountProfile =
    input.verifiedProfileAccountState === 'sync'
    || input.verifiedProfileAccountState === 'deactivated'

  let disposition: SessionAcceptanceDisposition = 'keep'
  if (hasPassport && hasSession && input.sessionOwnerId !== input.passportOwnerId) {
    disposition = 'reject'
  } else if (!hasPassport && hasSession && hasVerifiedAccountProfile && input.entryCommitted) {
    disposition = 'mount'
  }

  const acceptedSessionOwnerId =
    disposition !== 'reject'
    && input.passportOwnerId !== null
    && input.sessionOwnerId === input.passportOwnerId
      ? input.sessionOwnerId
      : null

  return {
    disposition,
    posture: resolveAccountCloudPosture({
      mountedAccountOwnerId: input.passportOwnerId,
      acceptedSessionOwnerId,
      hasVerifiedProfileAuthority: hasVerifiedAccountProfile,
      isCloudReachable: input.isCloudReachable
    })
  }
}

export function parseAccountExitSignal(data: unknown): AccountExitSignal | undefined {
  if (!data || typeof data !== 'object') return undefined
  const signal = data as Partial<AccountExitSignal>
  const ownerId = typeof signal.ownerId === 'string' ? signal.ownerId.trim() : ''
  const sourceTabId = typeof signal.sourceTabId === 'string' ? signal.sourceTabId.trim() : ''
  const issuedAt = typeof signal.issuedAt === 'string' ? signal.issuedAt.trim() : ''
  const mountId =
    typeof signal.mountId === 'string' && signal.mountId.trim()
      ? signal.mountId.trim()
      : undefined
  if (
    signal.type !== 'account-exit'
    || !ownerId
    || mountId === undefined
    || !sourceTabId
    || !issuedAt
  ) {
    return undefined
  }
  let intent: AccountExitSignalIntent
  let reason: AccountExitSignalReason
  if (
    signal.intent === 'explicit'
    && (signal.reason === 'logout' || signal.reason === 'account-delete')
  ) {
    intent = signal.intent
    reason = signal.reason
  } else if (signal.intent === 'passive' && signal.reason === 'auth-loss') {
    intent = signal.intent
    reason = signal.reason
  } else {
    return undefined
  }
  return {
    type: 'account-exit',
    ownerId,
    mountId,
    sourceTabId,
    intent,
    reason,
    issuedAt
  }
}

export function shouldUnmountForAccountExitSignal(signal: AccountExitSignal): boolean {
  return signal.intent === 'explicit'
}

const ALLOWED_INTENTS_BY_STATE: Record<AccountMachineState, readonly AccountIntent[]> = {
  local_only: ['ACTIVATE_SYNC'],
  sync_active: ['TERMINATE_SYNC', 'EXPORT_SHELF', 'LOGOUT'],
  sync_terminating: ['REACTIVATE_SYNC', 'EXPORT_SHELF', 'DELETE_ACCOUNT', 'LOGOUT'],
  sync_deactivated: ['ACTIVATE_SYNC', 'EXPORT_SHELF', 'DELETE_ACCOUNT', 'LOGOUT']
}

export function resolveAccountMachineState(snapshot: AccountSnapshot): AccountMachineState {
  if (!hasMountedAccount(snapshot)) return 'local_only'
  // An upgrade passport is intentionally optimistic local authority: until its
  // profile is verified, Account Memory stays editable while the courier waits.
  if (snapshot.accountState === 'none') return 'sync_active'
  if (snapshot.accountState === 'deactivated') return 'sync_deactivated'

  if (snapshot.accountState === 'sync' && snapshot.hasSyncPlan && snapshot.stripeCancelAtPeriodEnd) {
    return 'sync_terminating'
  }

  return 'sync_active'
}

export function createAccountMachineRuntimeState(snapshot: AccountSnapshot): AccountMachineRuntimeState {
  return {
    lifecycle: resolveAccountMachineState(snapshot)
  }
}

export function selectAllowedIntents(state: AccountMachineRuntimeState | AccountMachineState): readonly AccountIntent[] {
  const lifecycle = typeof state === 'string' ? state : state.lifecycle
  return ALLOWED_INTENTS_BY_STATE[lifecycle]
}

export function isIntentAllowed(state: AccountMachineRuntimeState | AccountMachineState, intent: AccountIntent): boolean {
  return selectAllowedIntents(state).includes(intent)
}

export function reduceAccountMachineEvent(
  state: AccountMachineRuntimeState,
  event: AccountMachineEvent
): AccountMachineRuntimeState {
  if (event.type === 'INTENT') {
    return {
      ...state,
      pendingIntent: event.intent,
      lastIntent: event.intent,
      lastError: undefined
    }
  }

  if (event.type === 'REQUEST_OK') {
    const wasPending = state.pendingIntent === event.intent
    return {
      ...state,
      pendingIntent: wasPending ? undefined : state.pendingIntent,
      lastIntent: event.intent,
      lastError: undefined
    }
  }

  if (event.type === 'REQUEST_FAIL') {
    const wasPending = state.pendingIntent === event.intent
    return {
      ...state,
      pendingIntent: wasPending ? undefined : state.pendingIntent,
      lastIntent: event.intent,
      lastError: event.error ?? 'unknown'
    }
  }

  if (event.type === 'PROFILE_REFRESHED' || event.type === 'CLOUD_AUTHORITY_LOST') {
    return {
      ...state,
      lifecycle: resolveAccountMachineState(event.snapshot),
      pendingIntent: undefined
    }
  }

  return state
}

export function selectIsTerminationScheduled(snapshot: AccountSnapshot): boolean {
  return resolveAccountMachineState(snapshot) === 'sync_terminating'
}

export function selectSyncSurfaceActions(snapshot: AccountSnapshot): SyncSurfaceAction[] {
  return selectSyncSurfaceActionAvailability(snapshot).map(entry => entry.action)
}

export function selectSyncSurfaceActionAvailability(snapshot: AccountSnapshot): SyncSurfaceActionAvailability[] {
  const state = resolveAccountMachineState(snapshot)
  const hasMount = hasMountedAccount(snapshot)
  const hasAcceptedSessionFact = hasAcceptedSession(snapshot)
  const hasCloudAuthority = hasVerifiedCloudAuthority(snapshot)
  if (state === 'sync_deactivated') {
    return [
      { action: 'reactivate sync', state: hasCloudAuthority ? 'armed' : 'disabled' },
      { action: 'export sync shelf', state: hasMount ? 'armed' : 'disabled' },
      { action: 'log out', state: hasMount || hasAcceptedSessionFact ? 'armed' : 'disabled' },
      { action: 'account-delete-all!', state: hasCloudAuthority ? 'armed' : 'disabled' }
    ]
  }

  const hasSyncAccess = snapshot.hasSyncPlan && (state === 'sync_active' || state === 'sync_terminating')
  const canActivateSync = !hasMount || (hasCloudAuthority && (!hasSyncAccess || state === 'sync_terminating'))
  const canCancelSync = hasCloudAuthority && hasSyncAccess && state === 'sync_active'
  const canDeleteAccount = hasCloudAuthority && state === 'sync_terminating'
  const canRenameUser = hasCloudAuthority && snapshot.hasUsername && snapshot.isSyncPaired && !snapshot.isViewMode
  const actions: SyncSurfaceActionAvailability[] = []
  const push = (action: SyncSurfaceAction, availabilityState: PickerOptionAvailabilityState): void => {
    actions.push({ action, state: availabilityState })
  }

  if (snapshot.isIndexSpace) {
    push('account-activate-sync', canActivateSync ? 'armed' : 'disabled')
    if (!hasMount && !hasAcceptedSessionFact) push('user-login', 'armed')
    push('account-cancel-sync', canCancelSync ? 'armed' : 'disabled')
    push('user-rename', canRenameUser ? 'armed' : 'disabled')
    push(
      'account-delete-all!',
      canDeleteAccount
        ? 'armed'
        : (state === 'sync_active' && hasCloudAuthority ? 'gated' : 'disabled')
    )
    return actions
  }

  if (state === 'local_only') {
    push('account-activate-sync', canActivateSync ? 'armed' : 'disabled')
    if (!hasMount && !hasAcceptedSessionFact) push('user-login', 'armed')
    push('account-cancel-sync', 'disabled')
    push('account-delete-all!', 'disabled')
    return actions
  }

  if (canActivateSync) push('account-activate-sync', 'armed')
  if (!hasMount && !hasAcceptedSessionFact) push('user-login', 'armed')
  push('account-cancel-sync', canCancelSync ? 'armed' : 'disabled')
  push('user-rename', canRenameUser ? 'armed' : 'disabled')
  push(
    'account-delete-all!',
    canDeleteAccount
      ? 'armed'
      : (state === 'sync_active' && hasCloudAuthority ? 'gated' : 'disabled')
  )
  return actions
}

export function selectAccountSyncCommandAvailability(
  snapshot: AccountSnapshot,
  commandId: AccountSyncCommandId
): { visible: boolean, enabled: boolean } {
  if (!snapshot.isIndexSpace) return { visible: false, enabled: false }
  const lifecycle = resolveAccountMachineState(snapshot)

  if (commandId === 'account-cancel-sync') {
    return {
      visible: true,
      enabled: lifecycle === 'sync_active' && hasVerifiedCloudAuthority(snapshot)
    }
  }

  return {
    visible: true,
    enabled: lifecycle === 'local_only'
      || (hasVerifiedCloudAuthority(snapshot)
        && (lifecycle === 'sync_terminating' || lifecycle === 'sync_deactivated'))
  }
}

export function selectSyncIndexStatusPresentation(
  snapshot: AccountSnapshot,
  periodEndLabel?: string
): SyncIndexStatusPresentation {
  const lifecycle = resolveAccountMachineState(snapshot)
  if (lifecycle === 'sync_terminating') {
    return {
      label: periodEndLabel ? `sync : deactivates at end : ${periodEndLabel}` : 'sync : deactivates at end',
      feedback: periodEndLabel
        ? `Account sync ends on ${periodEndLabel}. reactivate to keep it active`
        : 'Account sync is scheduled to end. reactivate to keep it active',
      description: 'Account sync remains active until period end.'
    }
  }
  if (lifecycle === 'sync_deactivated') {
    return {
      label: 'sync : deactivated',
      feedback: 'Account sync is deactivated. Restore it through subscription options or export your Account spaces.',
      description: 'Account sync is deactivated. Memory stays available.'
    }
  }
  if (lifecycle === 'sync_active') {
    return {
      label: periodEndLabel ? `sync : active renews : ${periodEndLabel}` : 'sync : active',
      feedback: periodEndLabel ? `Account sync renews on ${periodEndLabel}` : 'Account sync is active',
      description: 'Account sync and backup are active.'
    }
  }
  return {
    label: 'sync : off',
    feedback: 'memory works without Account',
    description: 'Account sync and backup are off.'
  }
}

export function selectSyncControlFeedback(intent: SyncControlIntent, error: SyncControlApiError): string {
  if (error === 'invalid_token' || error === 'missing_token') {
    return 'session expired, login again'
  }
  if (error === 'subscription_missing') {
    return intent === 'reactivate'
      ? 'no active subscription to reactivate'
      : 'no active sync subscription found'
  }
  if (error === 'account_not_sync') {
    return 'Account sync required'
  }
  if (error === 'profile_update_failed') {
    return 'could not update account status'
  }
  return intent === 'reactivate'
    ? 'could not reactivate sync'
    : 'could not schedule cancellation'
}

export function selectSyncSurfaceActionDescription(
  snapshot: AccountSnapshot,
  action: SyncSurfaceAction
): string | undefined {
  const lifecycle = resolveAccountMachineState(snapshot)
  if (action === 'index-export-all') {
    if (!hasMountedAccount(snapshot)) {
      return 'Account sync is not connected.'
    }
    if (lifecycle === 'local_only') {
      return 'Account sync is not active.'
    }
    return 'Export spaces in memory as standard JSON files in one zip.'
  }

  if (action !== 'account-delete-all!') return undefined

  if (
    (lifecycle === 'sync_terminating' || lifecycle === 'sync_deactivated')
    && hasVerifiedCloudAuthority(snapshot)
  ) {
    return 'Permanently delete your Account data after exporting your Account spaces.'
  }
  if (!hasMountedAccount(snapshot) && !hasAcceptedSession(snapshot)) {
    return 'Login first. Account deletion is only available after Account sync exists.'
  }
  if (!hasVerifiedCloudAuthority(snapshot)) {
    return 'Account connection required. Account deletion is unavailable while cloud authority is unverified.'
  }
  if (lifecycle === 'sync_active') {
    return 'Cancel Account sync first. Account deletion unlocks once the subscription is scheduled to end.'
  }
  return 'Activate Account sync first. Account deletion only applies after Account sync exists.'
}
